import { Info } from '@phosphor-icons/react';

import {
  PlatformFieldRow,
  PlatformPanel,
  PlatformSectionIntro,
} from '@/components/platform/PlatformSettingsPrimitives';
import { PlatformThemeChoice } from '@/components/platform/PlatformThemeChoice';

/**
 * Appearance & Regional.
 *
 * ONE real control and one honest statement, which is the whole point of this file.
 *
 * · THEME is real. It is backed by `lib/platformThemeStore.ts` — the console's single theme
 *   store — and it persists: per operator, namespaced by admin id, read synchronously before
 *   React mounts so a reload does not flash the wrong colours. This page does not own any theme
 *   state of its own; it renders the same control the sidebar footer used to hold.
 *
 * · REGIONAL is NOT configurable, and this page says so rather than offering a select that
 *   writes nowhere. `PlatformAdmin` has no timezone, locale, date-format or time-format column —
 *   those live on the chambers-side `UserPreference`, which is keyed to a `User` and which a
 *   platform operator does not have. The console formats dates `en-GB` at each call site. A
 *   dropdown here would be a control that changes local state and nothing else, which is exactly
 *   what the brief forbids.
 *
 * Notifications has no section at all for the same reason, one step further: there is no
 * notification preference storage for a `PlatformAdmin` and no optional mail — the only messages
 * an operator receives are the invitation and a password reset, both transactional. An empty
 * "Notifications" page would be a promise the product cannot keep.
 */
export default function PlatformSettingsAppearancePage() {
  return (
    <div className="space-y-5">
      <PlatformSectionIntro
        title="Appearance & Regional"
        description="How the Platform Console looks on this device, and how it formats dates and numbers"
      />

      <PlatformPanel
        title="Theme"
        description="The navigation rail stays navy in both themes so the console is always recognisable"
      >
        <PlatformFieldRow label="Colour theme" hint="Applies to the main content area">
          <PlatformThemeChoice />
        </PlatformFieldRow>
      </PlatformPanel>

      <PlatformPanel
        title="Regional"
        description="Fixed for the Platform Console — see below"
      >
        <PlatformFieldRow
          label="Date and time format"
          readOnly
          readOnlyValue="United Kingdom — 23 Sep 2026, 14:30 (24-hour)"
          readOnlyNote="The console formats every date and time in en-GB."
        />
        <PlatformFieldRow
          label="Timezone"
          readOnly
          readOnlyValue="This device's timezone"
          readOnlyNote="Timestamps are rendered in the timezone of the computer you are using. Stored values are UTC."
        />

        <div className="flex items-start gap-2 pt-3.5 text-xs text-plat-muted">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-plat-info-text" />
          <span>
            A platform operator account has no regional preferences to set. Timezone, locale and
            date-format preferences belong to chambers accounts, which are a different kind of
            record; there is nowhere on a platform account to store them. Rather than offer
            controls that would change nothing, this states what the console does.
          </span>
        </div>
      </PlatformPanel>
    </div>
  );
}
